import { createTheme } from "@mui/material/styles";
import type { TonStatut } from "../utilities/statutsQuete";

/* ══════════════════════════════════════════════════════════════════════════
   Charte du back-office Tribe — source unique des couleurs, contours et
   typographies. Les mêmes valeurs sont exposées en variables CSS (index.css)
   pour les écrans qui stylent en ligne ; ce fichier sert le code TS et le
   thème MUI. Une seule variante, claire : la charte n'a pas de mode sombre.
   ══════════════════════════════════════════════════════════════════════════ */

/* Charte Tribe, couleurs du logo : orange, ocre, vert, bleu (et le noir du
   trait). Pures, elles sont illisibles en texte sur le beige ; elles ne
   servent qu'en aplat (marqueurs de carte, rampes). */
export const CHARTE_LOGO = {
  orange: "#FF5B14",
  ocre: "#C18500",
  vert: "#7BFF1B",
  bleu: "#47A1FF",
} as const;

/* Variantes lisibles des mêmes teintes : contraste d'au moins 5:1 sur le
   fond #ECE8DF, sur les blocs et sur un badge teinté (couleur à 8 % sur
   bloc). Ce sont les couleurs de statut du back-office ; les états neutres
   prennent le gris du texte secondaire (COULEURS.neutre). */
export const CHARTE = {
  orange: "#A23A0D",
  ocre: "#7C5600",
  vert: "#356A0F",
  bleu: "#1D5FA4",
} as const;

export const COULEURS = {
  fond: "#ECE8DF",          // fond général
  bloc: "#F7F5F0",          // blocs, cartes, fiches
  blocAlterne: "#F1EEE7",   // lignes alternées, champs de saisie
  menu: "#E2DDD1",          // menu latéral, en-têtes de tableau
  texte: "#1F1E1B",
  texteSecondaire: "#57534A",
  separateur: "#CFC8B8",    // séparateurs fins, barres grisées
  blanc: "#FFFFFF",
  // Statuts (charte, lisibles sur fond clair) : badges et messages, jamais en aplat de bloc.
  succes: CHARTE.vert,          // validé, approuvé, publié, payé, actif
  attention: CHARTE.ocre,       // en attente, à valider, à traiter
  erreur: CHARTE.orange,        // rejeté, refusé, en échec, bloqué
  info: CHARTE.bleu,            // brouillon, étape intermédiaire, en cours, référentiel
  neutre: "#57534A",           // archivé, expiré, inactif, inconnu : gris du texte secondaire
} as const;

/* Ton d'un statut (utilities/statutsQuete) → couleur du texte, fond teinté
   et bordure du badge. Une seule table pour toutes les pages. */
const styleTon = (c: string) => ({ color: c, bg: `${c}14`, border: `${c}40` });
export const STYLE_TON: Record<TonStatut, { color: string; bg: string; border: string }> = {
  succes: styleTon(COULEURS.succes),
  attente: styleTon(COULEURS.attention),
  erreur: styleTon(COULEURS.erreur),
  accent: styleTon(COULEURS.info),
  neutre: styleTon(COULEURS.neutre),
};

/** « #RRGGBB » → [r, g, b], pour deck.gl. */
export const enRgb = (hex: string): [number, number, number] => [
  parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16),
];

/* Couleurs de domaine : repères uniquement (petit carré, liseré à gauche),
   jamais en aplat. */
export type Domaine = "accueil" | "traiter" | "quetes" | "lieux" | "reseau" | "argent" | "analyses" | "reglages";

export const COULEUR_DOMAINE: Record<Domaine, string> = {
  accueil: "#1F1E1B",
  traiter: "#B85F38",
  quetes: "#6E9A43",
  lieux: "#5B86B0",
  reseau: "#B5A53A",
  argent: "#8A6B2B",
  analyses: "#A9A291",
  reglages: "#A9A291",
};

/* Rampe des cartes de chaleur (densité faible → forte), tirée des couleurs du
   logo : ocre pâle, ocre, orange, puis indigo nuit pour les zones les plus
   denses. Sur fond de carte clair, la densité fonce au lieu de blanchir. */
export const RAMPE_DENSITE: [number, number, number][] = [
  [247, 222, 170],
  [232, 178, 90],
  [193, 133, 0],
  [230, 90, 20],
  [170, 55, 10],
  [43, 45, 110],
];

export const TRAIT = `1.5px solid ${COULEURS.texte}`;   // contour des blocs et boutons importants
export const TRAIT_FIN = `1px solid ${COULEURS.separateur}`;
export const RAYON = "8px";
export const RAYON_PETIT = "6px";

export const POLICE_TITRE = "'Montserrat', 'Work Sans', sans-serif";
export const POLICE_TEXTE = "'Work Sans', 'Helvetica Neue', Arial, sans-serif";

/* Palette des écrans existants (objets `C` locaux) : mêmes clés qu'avant,
   valeurs de la charte. Les couleurs de statut restent des hex (certains
   écrans leur ajoutent une opacité en suffixe, ex. `${C.success}22`). */
export const PALETTE_ECRAN = {
  bg: "var(--c-bg)",
  card: "var(--c-surface)",
  card2: "var(--c-surface-2)",
  surface: "var(--c-surface)",
  border: "var(--c-border)",
  borderAccent: COULEURS.texte,
  accent: COULEURS.texte,
  accentLight: COULEURS.texteSecondaire,
  accentMuted: "rgba(31,30,27,0.07)",
  text: COULEURS.texte,
  textSub: COULEURS.texteSecondaire,
  textMuted: COULEURS.texteSecondaire,
  success: COULEURS.succes,
  warning: COULEURS.attention,
  error: COULEURS.erreur,
  info: COULEURS.info,
} as const;

/* Thème MUI aligné sur la charte : pas d'ombre, pas de dégradé, angles 6-8 px. */
export const themeMui = createTheme({
  palette: {
    mode: "light",
    primary: { main: COULEURS.texte, contrastText: COULEURS.blanc },
    secondary: { main: COULEURS.texteSecondaire, contrastText: COULEURS.blanc },
    success: { main: COULEURS.succes },
    warning: { main: COULEURS.attention },
    error: { main: COULEURS.erreur },
    info: { main: COULEURS.info },
    background: { default: COULEURS.fond, paper: COULEURS.bloc },
    text: { primary: COULEURS.texte, secondary: COULEURS.texteSecondaire },
    divider: COULEURS.separateur,
  },
  /* Unité MUI conservée à 4 px : les écrans existants écrivent borderRadius: 2
     (= 8 px). Les angles de la charte (6 à 8 px) sont posés par composant. */
  shape: { borderRadius: 4 },
  shadows: Array(25).fill("none") as any,
  typography: {
    fontFamily: POLICE_TEXTE,
    h1: { fontFamily: POLICE_TITRE, fontWeight: 700 },
    h2: { fontFamily: POLICE_TITRE, fontWeight: 700 },
    h3: { fontFamily: POLICE_TITRE, fontWeight: 700 },
    h4: { fontFamily: POLICE_TITRE, fontWeight: 700 },
    h5: { fontFamily: POLICE_TITRE, fontWeight: 600 },
    h6: { fontFamily: POLICE_TITRE, fontWeight: 600 },
    button: { textTransform: "none", fontWeight: 600 },
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: RAYON_PETIT, textTransform: "none", fontWeight: 600, boxShadow: "none" },
        contained: { border: TRAIT, "&:hover": { boxShadow: "none" } },
        outlined: { border: TRAIT, color: COULEURS.texte, background: "transparent", "&:hover": { border: TRAIT, background: "rgba(31,30,27,0.05)" } },
      },
    },
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none", boxShadow: "none" }, rounded: { borderRadius: RAYON } } },
    MuiCard: { styleOverrides: { root: { boxShadow: "none", border: TRAIT, borderRadius: RAYON } } },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { background: COULEURS.texte, color: COULEURS.bloc, fontFamily: POLICE_TEXTE, fontSize: 12, borderRadius: RAYON_PETIT },
        arrow: { color: COULEURS.texte },
      },
    },
  },
});
